import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CartProvider, useCart } from '../../customer/context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { ShoppingCart, Search, User, ChevronDown, X, CheckCircle, MapPin, Phone, SlidersHorizontal, GripVertical } from 'lucide-react';
import PosProductList from './PosProductList';
import PosCartDrawer from './PosCartDrawer';
import PosFrequentOrders from './PosFrequentOrders';
import apiClient from '../../../services/apiClient';
import { getAllProducts, getProductCategories } from '../../../services/api/productApi';
import { getAllCustomers } from '../../../services/api/customerApi';
import Pagination from '../../../components/common/Pagination';
import ConfirmationModal from '../../../components/common/Modal/ConfirmationModal';
import Checkbox from '../../../components/common/Checkbox';
import Button from '../../../components/common/Button';

// ─── Customer Selection Panel ─────────────────────────────────────────────────
const CustomerSelector = ({ customers, loading, onSelect }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = useMemo(() => {
        if (!query.trim()) return customers;
        const q = query.toLowerCase();
        return customers.filter(c =>
            c.name?.toLowerCase().includes(q) ||
            c.mobileNumber?.includes(q) ||
            c.customerId?.toLowerCase().includes(q)
        );
    }, [customers, query]);

    return (
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 bg-gray-50">
            <div className="w-full max-w-md">
                {/* Icon + Title */}
                <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                        <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Select Customer</h2>
                    <p className="text-sm text-gray-500 mt-1">Choose the customer you're serving</p>
                </div>

                {/* Searchable Dropdown */}
                <div className="relative" ref={dropdownRef}>
                    <div
                        className="flex items-center gap-2 w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-400 focus-within:border-blue-500 transition-colors shadow-sm"
                        onClick={() => setOpen(true)}
                    >
                        <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <input
                            type="text"
                            className="flex-1 text-base md:text-sm text-gray-800 bg-transparent outline-none placeholder-gray-400"
                            placeholder={loading ? 'Loading customers…' : 'Search by name, mobile or ID…'}
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                            onFocus={() => setOpen(true)}
                            disabled={loading}
                        />
                        {query
                            ? <X className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={(e) => { e.stopPropagation(); setQuery(''); }} />
                            : <ChevronDown className="w-4 h-4 text-gray-400" />
                        }
                    </div>

                    {/* Dropdown List */}
                    {open && (
                        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-72 overflow-y-auto">
                            {filtered.length === 0 ? (
                                <div className="px-4 py-6 text-center text-sm text-gray-400">
                                    {loading ? 'Loading…' : 'No customers found'}
                                </div>
                            ) : (
                                filtered.map(c => (
                                    <button
                                        key={c.id}
                                        onClick={() => { onSelect(c); setOpen(false); setQuery(''); }}
                                        className="w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center gap-3 border-b last:border-0 transition-colors"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                            {(c.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-800 text-sm truncate">{c.name}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {c.mobileNumber || c.whatsappNumber || c.customerId || ''}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <p className="text-center text-xs text-gray-400 mt-4">
                    {customers.length} active customers available
                </p>
            </div>
        </div>
    );
};

// ─── POS Main Content ─────────────────────────────────────────────────────────
const PosContent = ({ onNavigate }) => {
    const { user } = useAuth();

    // Customer state
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isChangeCustomerModalOpen, setIsChangeCustomerModalOpen] = useState(false);

    // Product state — server-side paginated
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [productPage, setProductPage] = useState(1);      // 1-indexed for UI
    const [productTotalPages, setProductTotalPages] = useState(1);
    const [productTotalElements, setProductTotalElements] = useState(0);
    const PAGE_SIZE = 1000;

    // Category settings state
    const [categorySettings, setCategorySettings] = useState([]);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

    // Helper to merge categories from API with localStorage settings
    const getMergedCategories = useCallback((allCategories) => {
        const savedStr = localStorage.getItem('yaseero_pos_category_settings');
        let saved = null;
        if (savedStr) {
            try {
                saved = JSON.parse(savedStr);
            } catch(e) {
                console.warn(e);
            }
        }
        
        const order = saved?.order || [];
        const hidden = new Set(saved?.hidden || []);
        
        const newCats = allCategories.filter(c => !order.includes(c));
        const existingInOrder = order.filter(c => allCategories.includes(c));
        const finalOrder = [...existingInOrder, ...newCats];
        
        return finalOrder.map(name => ({
            name,
            visible: !hidden.has(name)
        }));
    }, []);

    // Load category settings on categories fetch
    useEffect(() => {
        if (categories.length > 0) {
            setCategorySettings(getMergedCategories(categories));
        }
    }, [categories, getMergedCategories]);

    const visibleCategories = useMemo(() => {
        return categorySettings.filter(c => c.visible).map(c => c.name);
    }, [categorySettings]);

    // Fetch categories
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const cats = await getProductCategories();
                setCategories(cats || []);
            } catch (err) {
                console.error('[POS] Failed to load categories', err);
            }
        };
        loadCategories();
    }, []);

    // Cart / UI state
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isClearCartModalOpen, setIsClearCartModalOpen] = useState(false);
    const [isOrderSuccessModalOpen, setIsOrderSuccessModalOpen] = useState(false);
    const [placedOrderId, setPlacedOrderId] = useState(null);
    const [selectedOnly, setSelectedOnly] = useState(false);

    const { cart, totalItems, totalAmount, clearCart, replaceCart } = useCart();

    const itemsToRender = useMemo(() => {
        if (!selectedOnly) return products;
        return cart.filter(item => 
            (!selectedCategory || item.category === selectedCategory) && 
            (!searchQuery || item.name?.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [selectedOnly, products, cart, selectedCategory, searchQuery]);

    const searchDebounceRef = useRef(null);
    const productContainerRef = useRef(null);
    const productsLoadingRef = useRef(productsLoading);
    const lastRequestedPageRef = useRef(1);

    // Sync productsLoading ref
    useEffect(() => {
        productsLoadingRef.current = productsLoading;
    }, [productsLoading]);

    // Reset lastRequestedPageRef when search, category, or customer changes
    useEffect(() => {
        lastRequestedPageRef.current = 1;
    }, [selectedCustomer, searchQuery, selectedCategory]);

    // Load all customers on mount
    useEffect(() => {
        const loadCustomers = async () => {
            try {
                const response = await getAllCustomers({ page: 0, size: 1000, status: 'ACTIVE' });
                setCustomers(response.content || []);
            } catch (err) {
                console.error('[POS] Failed to load customers', err);
            } finally {
                setCustomersLoading(false);
            }
        };
        loadCustomers();
    }, []);

    // ── Fetch products (server-side) ─────────────────────────────────────────
    const fetchProducts = useCallback(async (page, search, category, append = false) => {
        if (!selectedCustomer) return;
        setProductsLoading(true);
        try {
            const response = await getAllProducts({
                page: page - 1,   // backend is 0-indexed
                size: PAGE_SIZE,
                search: search || '',
                category: category || '',
                status: 'ACTIVE',
            });
            const withPrice = (response.content || []).map(p => {
                const isWholesale = selectedCustomer?.priceType === 'WHOLESALE';
                const regularPrice = isWholesale ? (p.price || 0) : (p.retailPrice || 0);
                const discountedPrice = isWholesale ? p.discountedPrice : p.discountedRetailPrice;
                const finalPrice = (discountedPrice !== null && discountedPrice !== undefined && discountedPrice > 0)
                    ? discountedPrice
                    : regularPrice;
                return {
                    ...p,
                    price: finalPrice,
                    originalPrice: regularPrice,
                    hasDiscount: discountedPrice !== null && discountedPrice !== undefined && discountedPrice < regularPrice,
                    vatRate: p.vatRate !== undefined ? p.vatRate : 5,
                    priceIncludesVat: p.priceIncludesVat !== undefined ? p.priceIncludesVat : false
                };
            });
            if (append) {
                setProducts(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    const newItems = withPrice.filter(p => !existingIds.has(p.id));
                    return [...prev, ...newItems];
                });
            } else {
                setProducts(withPrice);
            }
            setProductTotalPages(response.totalPages || 1);
            setProductTotalElements(response.totalElements || 0);
        } catch (err) {
            console.error('[POS] Failed to load products', err);
        } finally {
            setProductsLoading(false);
        }
    }, [selectedCustomer]);

    // Fetch when customer selected or page/category changes
    useEffect(() => {
        if (!selectedCustomer) return;
        const append = productPage > 1;
        fetchProducts(productPage, searchQuery, selectedCategory, append);
    }, [selectedCustomer, productPage, selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce search — resets to page 1
    useEffect(() => {
        if (!selectedCustomer) return;
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setProductPage(1);
            fetchProducts(1, searchQuery, selectedCategory);
        }, 300);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleScroll = (e) => {
        if (selectedOnly) return;
        if (productsLoadingRef.current) return;
        if (productPage >= productTotalPages) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            if (lastRequestedPageRef.current > productPage) {
                return; // Already requested next page
            }
            lastRequestedPageRef.current = productPage + 1;
            setProductPage(productPage + 1);
        }
    };


    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setSearchQuery('');
        setProductPage(1);
    };

    const handleChangeCustomer = () => {
        setIsChangeCustomerModalOpen(true);
    };

    const confirmChangeCustomer = () => {
        clearCart();
        setSelectedCustomer(null);
        setIsChangeCustomerModalOpen(false);
    };

    const handlePlaceOrder = async (cartItems, customer, assignedSalesPersonPublicId, deliveryDate, timeSlot, notes) => {
        try {
            const orderData = {
                customerPublicId: customer.id,
                supplierPublicId: user?.supplierPublicId,
                assignedSalesPersonPublicId: assignedSalesPersonPublicId || user?.publicId || user?.id,
                deliveryDate: deliveryDate || null,
                timeSlot: timeSlot || null,
                notes: notes || null,
                items: cartItems.map(item => ({
                    productPublicId: item.id,
                    quantity: item.quantity
                }))
            };
            // Use authenticated /orders endpoint (not /public/orders which requires a session token)
            const response = await apiClient.post('/orders', orderData);
            clearCart();
            setIsCartOpen(false);
            
            // Show success modal instead of resetting customer immediately
            setPlacedOrderId(response.data?.publicId);
            setIsOrderSuccessModalOpen(true);
        } catch (error) {
            console.error('[POS] Failed to place order', error);
            alert('Failed to place order. Please try again.');
            throw error;
        }
    };

    // ── STEP 1: No customer selected → show selection panel ──────────────────
    if (!selectedCustomer) {
        return (
            <div className="h-full flex flex-col bg-gray-50">
                {/* POS Header */}
                <div className="bg-white border-b px-4 py-3 md:px-6 flex items-center justify-between shadow-sm">
                    <div>
                        <h1 className="text-lg md:text-xl font-bold text-gray-800">POS Counter Sale</h1>
                        <p className="text-xs text-gray-500 hidden md:block">Select a customer to start</p>
                    </div>
                </div>

                <CustomerSelector
                    customers={customers}
                    loading={customersLoading}
                    onSelect={handleSelectCustomer}
                />
            </div>
        );
    }

    // ── STEP 2: Customer selected → show products ─────────────────────────────
    return (
        <div className="h-full flex flex-col bg-gray-50 md:bg-white relative overflow-hidden md:rounded-xl">
            {/* Header */}
            <div className="bg-white border-b px-4 py-2 md:px-6 shadow-sm">
                {/* Top row: title + cart icon */}
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h1 className="text-lg md:text-xl font-bold text-gray-800">POS Counter Sale</h1>
                    </div>
                    <button
                        onClick={() => setIsCartOpen(true)}
                        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ShoppingCart className="w-6 h-6 text-gray-600" />
                        {totalItems > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                {totalItems}
                            </span>
                        )}
                    </button>
                </div>

                {/* Customer chip + Frequent Orders inline */}
                <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0 bg-blue-50 rounded-xl px-3 py-2 border border-blue-100">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {(selectedCustomer.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 text-sm truncate">{selectedCustomer.name}</p>
                            {selectedCustomer.mobileNumber && (
                                <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                                    <Phone size={10} className="flex-shrink-0" />
                                    {selectedCustomer.mobileNumber}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleChangeCustomer}
                            className="text-xs text-blue-600 font-semibold hover:underline whitespace-nowrap ml-1"
                        >
                            Change
                        </button>
                    </div>
                    {/* Frequent Orders — compact inline button */}
                    <PosFrequentOrders
                        customerPublicId={selectedCustomer?.id}
                        onReorder={(items) => replaceCart(items)}
                        cartHasItems={totalItems > 0}
                        inline
                    />
                </div>

                {/* Search & Category Filter */}
                <div className="flex flex-col gap-2 mb-2">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search items by name…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-9 py-2.5 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-base md:text-sm"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => { setSearchQuery(''); setProductPage(1); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Clear search"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="w-1/3 min-w-[120px]">
                            <select
                                value={selectedCategory}
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                    setProductPage(1);
                                }}
                                className="w-full h-full px-3 bg-gray-100 rounded-lg border-none focus:ring-2 focus:ring-blue-400 text-sm outline-none text-gray-700"
                            >
                                <option value="">All Categories</option>
                                {visibleCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* Filter Pills row */}
                    <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1 pt-1 -mx-2 px-2">
                        <button
                            onClick={() => setIsCategoryModalOpen(true)}
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none shrink-0"
                            aria-label="Customize categories"
                        >
                            <SlidersHorizontal className="w-4 h-4" />
                        </button>

                        <button
                            onClick={() => setSelectedOnly(prev => !prev)}
                            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 select-none ${
                                selectedOnly 
                                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-100 scale-102' 
                                    : 'bg-emerald-50/80 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 border border-emerald-200/50'
                            }`}
                        >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            <span>Selected ({totalItems})</span>
                        </button>
                        
                        <button
                            onClick={() => { setSelectedCategory(''); setProductPage(1); }}
                            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                selectedCategory === '' ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            All
                        </button>
                        {visibleCategories.map(cat => (
                            <button
                                key={`pill-${cat}`}
                                onClick={() => { setSelectedCategory(cat); setProductPage(1); }}
                                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    selectedCategory === cat ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Products */}
            <div className="flex-1 overflow-hidden flex flex-col p-2 md:p-3">
                {!productsLoading && itemsToRender.length > 0 && (
                    <div className="px-2 pb-2 flex items-center justify-between text-[10px] text-gray-400 font-black uppercase tracking-wider">
                        <span>Showing {itemsToRender.length} of {selectedOnly ? itemsToRender.length : productTotalElements} products</span>
                        {!selectedOnly && productPage < productTotalPages && (
                            <span className="text-blue-500 font-black">Scroll down for more</span>
                        )}
                    </div>
                )}
                {productsLoading && productPage === 1 && !selectedOnly ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                        <div className="flex flex-col items-center gap-2">
                            <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></span>
                            <span>Loading products…</span>
                        </div>
                    </div>
                ) : (
                    <div 
                        ref={productContainerRef}
                        onScroll={handleScroll}
                        className={`flex-1 min-h-0 overflow-y-auto custom-scrollbar ${totalItems > 0 ? 'pb-14 md:pb-10' : ''}`}
                    >
                        {itemsToRender.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm py-12 gap-3">
                                <span>{selectedOnly ? "No selected items in cart." : "No products found."}</span>
                                {selectedOnly && (
                                    <button
                                        onClick={() => setSelectedOnly(false)}
                                        className="text-xs font-bold text-blue-600 hover:underline"
                                    >
                                        Show all products
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <PosProductList
                                    products={itemsToRender}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3"
                                />
                                {productsLoading && productPage > 1 && !selectedOnly && (
                                    <div className="py-4 flex justify-center items-center text-gray-500 text-xs gap-2">
                                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></span>
                                        <span>Loading more products…</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}


                {/* Spacer: just enough to clear the checkout bar overlap (~22px) */}
                {totalItems > 0 && <div className="h-6 flex-shrink-0" />}
            </div>


            {/* Bottom checkout bar — compact single-row strip */}
            {totalItems > 0 && (
                <div className="fixed md:absolute bottom-0 left-0 right-0 md:w-full bg-white border-t shadow-[0_-2px_8px_rgba(0,0,0,0.08)] z-20 pb-16 md:pb-0">
                    <div className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2">
                        {/* Item count badge */}
                        <div className="flex items-center gap-1.5">
                            <span className="flex items-center justify-center min-w-[28px] h-7 bg-blue-600 text-white text-xs font-black rounded-lg px-1.5">
                                {totalItems}
                            </span>
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{totalItems === 1 ? 'item' : 'items'}</span>
                        </div>
                        <span className="text-gray-300 mx-1">•</span>
                        {/* Amount */}
                        <div className="flex-1 min-w-0">
                            <span className="text-base font-black text-gray-900">AED {totalAmount}</span>
                        </div>
                        {/* Clear cart */}
                        <button
                            onClick={() => setIsClearCartModalOpen(true)}
                            className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-all flex-shrink-0"
                            title="Clear Cart"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            </svg>
                        </button>
                        {/* Checkout */}
                        <button
                            onClick={() => setIsCartOpen(true)}
                            className="bg-blue-600 text-white px-4 py-1.5 rounded-lg font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-1.5 text-sm flex-shrink-0"
                        >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            <span>Checkout</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Cart Drawer */}
            <PosCartDrawer
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                onPlaceOrder={handlePlaceOrder}
                selectedCustomer={selectedCustomer}
                currentUser={user}
            />

            {/* Clear cart modal */}
            <ConfirmationModal
                isOpen={isClearCartModalOpen}
                onClose={() => setIsClearCartModalOpen(false)}
                onConfirm={() => { clearCart(); setIsClearCartModalOpen(false); }}
                title="Clear Cart?"
                message="Are you sure you want to remove all items from your order?"
                confirmText="Yes, Clear All"
                cancelText="No, Keep Items"
                type="info"
                icon={<ShoppingCart className="w-6 h-6 text-blue-600" />}
            />

            {/* Change customer modal — warns about cart clear */}
            <ConfirmationModal
                isOpen={isChangeCustomerModalOpen}
                onClose={() => setIsChangeCustomerModalOpen(false)}
                onConfirm={confirmChangeCustomer}
                title="Change Customer?"
                message={totalItems > 0
                    ? `Changing the customer will clear your current cart (${totalItems} items). Do you want to continue?`
                    : 'Select a different customer?'
                }
                confirmText="Yes, Change"
                cancelText="Cancel"
                type="warning"
                icon={<User className="w-6 h-6 text-amber-500" />}
            />

            {/* Order Success modal */}
            <ConfirmationModal
                isOpen={isOrderSuccessModalOpen}
                onClose={() => {
                    setIsOrderSuccessModalOpen(false);
                    setSelectedCustomer(null);
                }}
                onConfirm={() => {
                    setIsOrderSuccessModalOpen(false);
                    if (onNavigate) {
                        onNavigate('sales-orders');
                    }
                }}
                title="Order Placed Successfully!"
                message={`The order for ${selectedCustomer?.name} has been placed. You can view it in the orders list.`}
                confirmText="View Orders"
                cancelText="New POS Order"
                type="success"
                icon={<CheckCircle className="w-6 h-6 text-green-500" />}
            />

            {isCategoryModalOpen && (
                <CategorySettingsModal
                    isOpen={isCategoryModalOpen}
                    onClose={() => setIsCategoryModalOpen(false)}
                    categorySettings={categorySettings}
                    onSave={(newSettings) => {
                        setCategorySettings(newSettings);
                        const order = newSettings.map(c => c.name);
                        const hidden = newSettings.filter(c => !c.visible).map(c => c.name);
                        localStorage.setItem('yaseero_pos_category_settings', JSON.stringify({ order, hidden }));
                        setIsCategoryModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

// ─── Category Settings Customizer Modal (Issue 3) ──────────────────────────────
const CategorySettingsModal = ({ isOpen, onClose, categorySettings, onSave }) => {
    const [localSettings, setLocalSettings] = useState(categorySettings);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const modalRef = useRef(null);

    // Escape key listener for accessibility
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Focus management: focus on modal container when opened
    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus();
        }
    }, [isOpen]);

    const handleDragStart = (e, index) => {
        dragItem.current = index;
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnter = (e, index) => {
        dragOverItem.current = index;
        e.preventDefault();
    };

    const handleDrop = () => {
        if (dragItem.current === null || dragOverItem.current === null) return;
        if (dragItem.current === dragOverItem.current) return;

        const copy = [...localSettings];
        const [moved] = copy.splice(dragItem.current, 1);
        copy.splice(dragOverItem.current, 0, moved);
        setLocalSettings(copy);

        dragItem.current = null;
        dragOverItem.current = null;
    };

    const handleToggleVisible = (index) => {
        setLocalSettings(prev => prev.map((item, idx) => 
            idx === index ? { ...item, visible: !item.visible } : item
        ));
    };

    return (
        <div 
            className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Category settings"
        >
            <div 
                ref={modalRef}
                tabIndex="-1"
                className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal size={18} className="text-gray-500" />
                        <h3 className="text-base font-black text-gray-900">Customize Categories</h3>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1 rounded-lg text-gray-400 hover:text-gray-600 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
                        aria-label="Close modal"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    <p className="text-xs text-gray-400 font-medium">Drag to reorder category pills. Use checkmarks to toggle their visibility.</p>
                    <div className="space-y-2">
                        {localSettings.map((cat, idx) => (
                            <div 
                                key={cat.name}
                                draggable
                                onDragStart={e => handleDragStart(e, idx)}
                                onDragEnter={e => handleDragEnter(e, idx)}
                                onDragEnd={handleDrop}
                                onDragOver={e => e.preventDefault()}
                                className="flex items-center justify-between p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200/60 hover:border-blue-200 rounded-xl transition-all select-none cursor-grab active:cursor-grabbing"
                            >
                                <div className="flex items-center gap-3">
                                    <GripVertical size={16} className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing" />
                                    <span className="text-sm font-bold text-gray-800">{cat.name}</span>
                                </div>
                                <Checkbox
                                    checked={cat.visible}
                                    onChange={() => handleToggleVisible(idx)}
                                    aria-label={`Toggle visibility of ${cat.name}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={onClose}
                        className="focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={() => onSave(localSettings)}
                        className="focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        Save Settings
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Wrapper with CartProvider ────────────────────────────────────────────────
const PosView = ({ onNavigate }) => (
    <CartProvider>
        <PosContent onNavigate={onNavigate} />
    </CartProvider>
);

export default PosView;
